import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import PropTypes from 'prop-types';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

const PrecioRow = ({ label, valor, moneda, bold, variant }) => (
    <div className="d-flex justify-content-between align-items-center py-1">
        <span className="text-muted small">{label}</span>
        <span className={`fw-${bold ? 'bold' : 'normal'} ${variant ? `text-${variant}` : ''}`}>
            ${parseFloat(valor || 0).toFixed(2)} {moneda}
        </span>
    </div>
);

PrecioRow.propTypes = {
    label:  PropTypes.string.isRequired,
    valor:  PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    moneda: PropTypes.string,
    bold:   PropTypes.bool,
    variant: PropTypes.string
};

/**
 * Modal para agregar un producto (ModuloExtra) a una suscripción activa.
 *
 * Flujo:
 *   1. Al abrir: carga productos disponibles filtrados por moneda y tipo de renovación de la suscripción
 *   2. Al seleccionar producto: calcula el cobro diferencial (prorateado) vía SP
 *   3. Botón "Agregar en el siguiente cobro": asigna sin pago inmediato
 *   4. Botón "Generar orden": asigna y crea orden con el precio diferencial
 *
 * Props:
 *   show         – bool
 *   onHide       – fn
 *   suscripcion  – objeto suscripción (idSuscripcion, idLicencia, vMoneda, vTipoRenovacion, ...)
 *   onCreated    – fn callback tras guardar exitoso
 */
const ModalAgregarProductoSuscripcion = ({ show, onHide, suscripcion, onCreated }) => {
    const { token } = useToken();
    const { showErrorToast, showSuccessToast } = useToast();

    const [loadingProductos, setLoadingProductos]     = useState(false);
    const [loadingDiferencial, setLoadingDiferencial] = useState(false);
    const [saving, setSaving]                         = useState(false);
    const [errorProductos, setErrorProductos]         = useState('');
    const [productos, setProductos]                   = useState([]);
    const [selectedProducto, setSelectedProducto]     = useState('');
    const [diferencial, setDiferencial]               = useState(null); // resultado del SP diferencial

    // Producto seleccionado (objeto completo, incluyendo idProductoRenovaciones)
    const productoSeleccionado = productos.find(
        p => String(p.idProducto) === String(selectedProducto)
    ) ?? null;

    // ──────────────────────────────────────────────────────────────
    // Carga de productos disponibles para esta suscripción
    // ──────────────────────────────────────────────────────────────
    const cargarProductos = async () => {
        if (!suscripcion?.idLicencia) return;
        setLoadingProductos(true);
        setErrorProductos('');
        setProductos([]);
        setSelectedProducto('');
        setDiferencial(null);
        try {
            const resp = await fetch(
                `/api/licencia/ObtenerContextoAltaModulo?idLicencia=${suscripcion.idLicencia}`,
                { headers: { 'Authorization': 'Bearer ' + token }, cache: 'no-store' }
            );
            const data = await resp.json();
            if (data.iResponseType === 1 && data.detalles) {
                const det = JSON.parse(data.detalles);
                setProductos(Array.isArray(det.table3) ? det.table3 : []);
            } else {
                setErrorProductos(data.vMessage || 'Error al obtener módulos disponibles');
            }
        } catch (err) {
            setErrorProductos('Error al cargar módulos: ' + err.message);
        } finally {
            setLoadingProductos(false);
        }
    };

    // ──────────────────────────────────────────────────────────────
    // Cálculo del diferencial al seleccionar un producto
    // ──────────────────────────────────────────────────────────────
    const calcularDiferencial = async (idProductoRenovaciones) => {
        if (!idProductoRenovaciones || !suscripcion?.idLicencia) return;
        setLoadingDiferencial(true);
        setDiferencial(null);
        try {
            const resp = await fetch(
                `/api/licencia/CalcularDiferencialModulo?idLicencia=${suscripcion.idLicencia}&idProductoRenovaciones=${idProductoRenovaciones}`,
                { headers: { 'Authorization': 'Bearer ' + token }, cache: 'no-store' }
            );
            const data = await resp.json();
            if (data.iResponseType === 1 && data.detalles) {
                setDiferencial(JSON.parse(data.detalles));
            } else {
                showErrorToast(data.vMessage || 'Error al calcular precio diferencial');
            }
        } catch (err) {
            showErrorToast('Error al calcular diferencial: ' + err.message);
        } finally {
            setLoadingDiferencial(false);
        }
    };

    useEffect(() => {
        if (show) {
            cargarProductos();
        } else {
            setProductos([]);
            setSelectedProducto('');
            setDiferencial(null);
            setErrorProductos('');
        }
    }, [show, suscripcion?.idLicencia]);

    const handleSelectProducto = (idProducto) => {
        setSelectedProducto(idProducto);
        setDiferencial(null);
        if (!idProducto) return;
        const prod = productos.find(p => String(p.idProducto) === String(idProducto));
        if (prod?.idProductoRenovaciones) {
            calcularDiferencial(prod.idProductoRenovaciones);
        }
    };

    // ──────────────────────────────────────────────────────────────
    // Acciones
    // ──────────────────────────────────────────────────────────────
    const isStripe = !!suscripcion?.vStripeSubscriptionId;

    const ejecutarAccion = async (bGenerarOrden) => {
        if (!productoSeleccionado) return showErrorToast('Selecciona un módulo');
        if (!diferencial)          return showErrorToast('Esperando cálculo de precio');
        if (!suscripcion?.idSuscripcion) return showErrorToast('No se encontró la suscripción');

        setSaving(true);
        try {
            const payload = {
                idLicencia:             suscripcion.idLicencia,
                idSuscripcion:          suscripcion.idSuscripcion,
                idProducto:             parseInt(selectedProducto),
                idProductoRenovaciones: productoSeleccionado.idProductoRenovaciones,
                bActivarInmediato:      false,
                bGenerarOrden,
                dPrecioUnitario: bGenerarOrden ? parseFloat(diferencial.dPrecioUnitarioDiferencial || 0) : 0,
                dIVA:            bGenerarOrden ? parseFloat(diferencial.dIVADiferencial            || 0) : 0,
                dTotal:          bGenerarOrden ? parseFloat(diferencial.dCobroDiferencial          || 0) : 0
            };

            const resp = await fetch('/api/Suscripciones/AsignarProductoASuscripcionManual', {
                method: 'POST',
                headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await resp.json();
            if (data.iResponseType === 1) {
                showSuccessToast(
                    bGenerarOrden
                        ? 'Módulo agregado y orden generada correctamente'
                        : 'Módulo agregado. Se activará en el siguiente cobro'
                );
                onCreated();
                onHide();
            } else {
                showErrorToast(data.vMessage || 'Error al procesar la solicitud');
            }
        } catch (err) {
            showErrorToast('Error: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const ejecutarStripeAccion = async () => {
        if (!productoSeleccionado)            return showErrorToast('Selecciona un módulo');
        if (!diferencial)                     return showErrorToast('Esperando cálculo de precio');
        if (!suscripcion?.idSuscripcion)      return showErrorToast('No se encontró la suscripción');

        setSaving(true);
        try {
            const payload = {
                idLicencia:             suscripcion.idLicencia,
                idSuscripcion:          suscripcion.idSuscripcion,
                idProducto:             parseInt(selectedProducto),
                idProductoRenovaciones: productoSeleccionado.idProductoRenovaciones,
                idUsuarioCreado:        0,  // backend fallback a AkasiaBot si no se resuelve
                bActivarInmediato:      true,
                bGenerarOrden:          false,
                dPrecioUnitario:        0,
                dIVA:                   0,
                dTotal:                 0
            };

            const resp = await fetch('/api/Suscripciones/AsignarProductoASuscripcionAutomatica', {
                method: 'POST',
                headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await resp.json();
            if (data.iResponseType === 1) {
                showSuccessToast('Producto agregado y cobro diferencial generado por Stripe');
                onCreated();
                onHide();
            } else {
                showErrorToast(data.vMessage || 'Error al procesar el cobro con Stripe');
            }
        } catch (err) {
            showErrorToast('Error: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    // ──────────────────────────────────────────────────────────────
    // Helpers de UI
    // ──────────────────────────────────────────────────────────────
    const moneda    = diferencial?.vMoneda ?? suscripcion?.vMoneda ?? 'MXN';
    const canAction = !!productoSeleccionado && !!diferencial && !loadingDiferencial && !saving;

    return (
        <Modal show={show} onHide={onHide} centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Agregar módulo a suscripción</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {/* Info de la suscripción */}
                {suscripcion && (
                    <div className="d-flex align-items-center gap-2 mb-4 p-3 rounded border bg-light">
                        <div>
                            <div className="fw-semibold">Suscripción #{suscripcion.idSuscripcion}</div>
                            <div className="d-flex gap-2 mt-1">
                                {suscripcion.vTipoRenovacion && (
                                    <Badge bg="secondary" className="fw-normal">{suscripcion.vTipoRenovacion}</Badge>
                                )}
                                {suscripcion.vMoneda && (
                                    <Badge bg="secondary" className="fw-normal">{suscripcion.vMoneda}</Badge>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {loadingProductos ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <div className="text-muted mt-2">Cargando módulos disponibles…</div>
                    </div>
                ) : errorProductos ? (
                    <Alert variant="danger">{errorProductos}</Alert>
                ) : (
                    <div className="d-flex flex-column gap-3">
                        {/* Selector de módulo */}
                        <Form.Group>
                            <Form.Label className="fw-semibold">Módulo a agregar</Form.Label>
                            <Form.Select
                                value={selectedProducto}
                                onChange={e => handleSelectProducto(e.target.value)}
                                disabled={saving}
                            >
                                <option value="">— Selecciona un módulo —</option>
                                {productos.map(p => (
                                    <option key={p.idProducto} value={p.idProducto}>
                                        {p.vNombreProducto}
                                        {p.vDescripcionModulo ? ` — ${p.vDescripcionModulo}` : ''}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        {/* Panel de precios */}
                        {selectedProducto && (
                            loadingDiferencial ? (
                                <div className="text-center py-3">
                                    <Spinner animation="border" size="sm" variant="primary" className="me-2" />
                                    <span className="text-muted">Calculando precio diferencial…</span>
                                </div>
                            ) : diferencial ? (
                                <div className="rounded border overflow-hidden">
                                    <div className="px-3 py-2 bg-light border-bottom">
                                        <span className="fw-semibold text-muted small text-uppercase" style={{ letterSpacing: '0.05em' }}>
                                            Resumen de cobro
                                        </span>
                                    </div>
                                    <div className="p-3 d-flex flex-column gap-2">
                                        <PrecioRow
                                            label={`Precio ${diferencial.vTipoRenovacion ?? 'del producto'}`}
                                            valor={diferencial.dPrecioTotalProducto}
                                            moneda={moneda}
                                        />
                                        <hr className="my-0" />
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <div className="fw-semibold">
                                                    {diferencial.bRequierePago ? 'A pagar ahora' : 'Sin cobro inmediato'}
                                                </div>
                                                {diferencial.iDiasRestantes > 0 && (
                                                    <div className="text-muted small">{diferencial.iDiasRestantes} días restantes del ciclo</div>
                                                )}
                                            </div>
                                            <span className={`fs-5 fw-bold text-${diferencial.bRequierePago ? 'success' : 'secondary'}`}>
                                                ${parseFloat(diferencial.dCobroDiferencial || 0).toFixed(2)} {moneda}
                                            </span>
                                        </div>
                                        {!diferencial.bRequierePago && (
                                            <Alert variant="info" className="mb-0 py-2 small">
                                                El costo es mínimo y se incluirá en el siguiente ciclo de renovación.
                                            </Alert>
                                        )}
                                    </div>
                                </div>
                            ) : null
                        )}
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer className="d-flex justify-content-between align-items-center">
                <Button variant="link" className="text-secondary text-decoration-none ps-0" onClick={onHide} disabled={saving}>
                    Cancelar
                </Button>
                <div className="d-flex gap-2">
                    <Button
                        variant="outline-secondary"
                        onClick={() => ejecutarAccion(false)}
                        disabled={!canAction}
                    >
                        Agregar al siguiente cobro
                    </Button>
                    {isStripe ? (
                        <Button
                            variant="primary"
                            onClick={ejecutarStripeAccion}
                            disabled={!canAction || !diferencial?.bRequierePago}
                        >
                            {saving
                                ? <><Spinner animation="border" size="sm" className="me-2" />Procesando…</>
                                : diferencial?.bRequierePago
                                    ? `Cobrar con Stripe · $${parseFloat(diferencial.dCobroDiferencial || 0).toFixed(2)} ${moneda}`
                                    : 'Cobrar con Stripe'
                            }
                        </Button>
                    ) : (
                        <Button
                            variant="primary"
                            onClick={() => ejecutarAccion(true)}
                            disabled={!canAction || !diferencial?.bRequierePago}
                        >
                            {saving
                                ? <><Spinner animation="border" size="sm" className="me-2" />Procesando…</>
                                : diferencial?.bRequierePago
                                    ? `Generar orden · $${parseFloat(diferencial.dCobroDiferencial || 0).toFixed(2)} ${moneda}`
                                    : 'Generar orden'
                            }
                        </Button>
                    )}
                </div>
            </Modal.Footer>
        </Modal>
    );
};

ModalAgregarProductoSuscripcion.propTypes = {
    show:        PropTypes.bool.isRequired,
    onHide:      PropTypes.func.isRequired,
    suscripcion: PropTypes.shape({
        idSuscripcion:         PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        idLicencia:            PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        vTipoRenovacion:       PropTypes.string,
        vMoneda:               PropTypes.string,
        vStripeSubscriptionId: PropTypes.string
    }),
    onCreated:   PropTypes.func.isRequired
};

export default ModalAgregarProductoSuscripcion;

